import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Edit2, Search, Save, Plus } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../../components/Tables/APITable";
import Modal from "../../../components/Modal/Modal";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import Pagination from "../../../components/Pagination/Pagination";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import { apiGet, apiPut } from "../../../apiService";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./NotificationTemplate.module.scss";

const NotificationTemplate = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [templateTypes, setTemplateTypes] = useState([]);

  // Pagination (SOURCE OF TRUTH)
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Filters
  const [filterTemplateType, setFilterTemplateType] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  // Form
  const [templateName, setTemplateName] = useState("");
  const [templateType, setTemplateType] = useState("Email");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState([]);

  // Preview
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [previewSubject, setPreviewSubject] = useState("");

  const [loading, setLoading] = useState(false);

  // Variables Grid Splitter
  const half = Math.ceil(variables.length / 2);
  const firstHalf = variables.slice(0, half);
  const secondHalf = variables.slice(half);

  /* =========================
     FETCH TEMPLATES
  ========================== */
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const url =
        `/template-masters?page=${currentPage}&limit=${rowsPerPage}` +
        (filterTemplateType ? `&type=${filterTemplateType}` : "") +
        (appliedSearch ? `&search=${appliedSearch}` : "");

      const response = await apiGet("notification", url);

      if (response?.status === "success") {
        const { data, pagination, types } = response.data;

        setTemplates(
          data.map((tpl) => ({
            id: tpl.id,
            name: tpl.name,
            type: tpl.type,
            subject: tpl.subject || "-",
            content: tpl.message,
            updated_at: tpl.updatedAt,
          })),
        );

        setPagination(pagination);

        if (Array.isArray(types)) {
          setTemplateTypes(types);
        }
      }
    } catch (error) {
      console.error("Failed to fetch templates", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, filterTemplateType, appliedSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, filterTemplateType]);

  const columns = [
    { label: "Name", key: "name" },
    { label: "Type", key: "type" },
    { label: "Subject", key: "subject" },
    { label: "Message", key: "content" },
    { label: "Updated At", key: "updated_at" },
    { label: "Actions", key: "action" },
  ];

  /* =========================
     FETCH TEMPLATE BY ID
  ========================== */
  const fetchTemplateById = async (id) => {
    try {
      const response = await apiGet("notification", `/template-masters/${id}`);

      if (response?.status === "success") {
        const templateData = response.data;

        setTemplateName(templateData.name);
        setTemplateType(templateData.type);
        setSubject(templateData.subject);
        setContent(templateData.message);
        setVariables(
          templateData.variables?.[templateData.type.toLowerCase()] || [],
        );
        setSelectedTemplate(templateData);
        setShowModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch template", error);
    }
  };

  /* =========================
     UPDATE TEMPLATE
  ========================== */
  const handleSaveTemplate = async () => {
    setIsSaving(true);
    try {
      const updatedTemplate = {
        type: templateType,
        name: templateName,
        subject,
        message: content,
      };

      const response = await apiPut(
        "notification",
        `/template-masters/${selectedTemplate.id}`,
        updatedTemplate,
      );

      if (response?.message === "Template updated successfully") {
        setShowModal(false);
        resetForm();
        fetchTemplates();
      }
    } catch (error) {
      console.error("Failed to update template", error);
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setTemplateName("");
    setTemplateType("Email");
    setSubject("");
    setContent("");
    setVariables([]);
    setSelectedTemplate(null);
  };

  const getBadgeClass = (type) => {
    const t = type?.toLowerCase();
    switch (t) {
      case "email":
        return styles["admin-noti-temp__badge--email"];
      case "sms":
        return styles["admin-noti-temp__badge--sms"];
      case "notification":
        return styles["admin-noti-temp__badge--notification"];
      case "activitylog":
        return styles["admin-noti-temp__badge--activitylog"];
      default:
        return styles["admin-noti-temp__badge--default"];
    }
  };

  /* =========================
     JSX
  ========================== */
  return (
    <div className={styles["admin-noti-temp__page"]}>
      {/* HEADER */}
      <div className={styles["admin-noti-temp__header"]}>
        <div className={styles["admin-noti-temp__header-left"]}>
          <div className={styles["admin-noti-temp__icon-wrapper"]}>
            <Bell size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["admin-noti-temp__title-group"]}>
            <h2 className={styles["admin-noti-temp__title"]}>
              Notification Template
            </h2>
            <p className={styles["admin-noti-temp__subtitle"]}>
              Create and manage notification templates.
            </p>
          </div>
        </div>

        {can("notification-template", "create") && (
          <Button
            text="Add Template"
            icon={Plus}
            onClick={() => navigate("/admin/notification-template/add")}
          />
        )}
      </div>

      {/* MAIN CARD */}
      <div className={styles["admin-noti-temp__card"]}>
        {/* CONTROLS */}
        <div className={styles["admin-noti-temp__controls"]}>
          <div className={styles["admin-noti-temp__showing-text"]}>
            Showing {templates.length} of {pagination.total} templates
          </div>

          <div className={styles["admin-noti-temp__filters"]}>
            <div className={styles["admin-noti-temp__search-group"]}>
              <Input
                type="search"
                placeholder="Search Template Name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setAppliedSearch(searchInput);
                    setCurrentPage(1);
                  }
                }}
              />
              <Button
                text="Search"
                icon={Search}
                onClick={() => {
                  setAppliedSearch(searchInput);
                  setCurrentPage(1);
                }}
              />
            </div>
            <Input
              type="select"
              placeholder="All Types"
              value={filterTemplateType}
              onChange={(e) => setFilterTemplateType(e.target.value)}
              options={[
                { label: "All Types", value: "" },
                ...templateTypes.map((t) => ({ label: t, value: t })),
              ]}
            />
          </div>
        </div>

        {/* TABLE */}
        <div className={styles["admin-noti-temp__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={6} rows={5} />
          ) : (
            <APITable
              columns={columns}
              data={templates.map((tpl) => ({
                ...tpl,
                contentPreview:
                  tpl.content.length > 50
                    ? `${tpl.content.slice(0, 50)}...`
                    : tpl.content,
                contentFull: tpl.content,
                onView: () => fetchTemplateById(tpl.id),
              }))}
              renderCell={(row, key) => {
                /* DATE & TIME */
                if (key === "updated_at") {
                  const datetime = row.updated_at;
                  if (!datetime) return "--";
                  const [date, time, meridian] = datetime.split(" ");
                  return (
                    <span>
                      {date}{" "}
                      <span className={styles["admin-noti-temp__time"]}>
                        {time} {meridian}
                      </span>
                    </span>
                  );
                }

                /* PILL BADGE */
                if (key === "type") {
                  return (
                    <div
                      className={`${styles["admin-noti-temp__badge"]} ${getBadgeClass(row.type)}`}
                    >
                      {row.type}
                    </div>
                  );
                }

                /* CLICKABLE MESSAGE PREVIEW */
                if (key === "content") {
                  return (
                    <span
                      className={styles["admin-noti-temp__preview-link"]}
                      title="Click to preview full message"
                      onClick={() => {
                        setPreviewHtml(row.contentFull);
                        setPreviewSubject(row.subject);
                        setShowPreview(true);
                      }}
                    >
                      {row.contentPreview}
                    </span>
                  );
                }

                /* EDIT ACTION */
                if (key === "action") {
                  return can("notification-template", "update") ? (
                    <div
                      className={styles["admin-noti-temp__icon-btn"]}
                      onClick={row.onView}
                      title="Edit Template"
                    >
                      <Edit2 size={18} strokeWidth={2.5} />
                    </div>
                  ) : (
                    "—"
                  );
                }
                return row[key];
              }}
            />
          )}
        </div>

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(limit) => {
            setRowsPerPage(limit);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* EDIT MODAL */}
      {showModal && (
        <Modal
          title={selectedTemplate ? "Edit Template" : "Add Template"}
          onClose={() => setShowModal(false)}
        >
          <div className={styles["admin-noti-temp__modal-form"]}>
            <Input label="Template Name" value={templateName} disabled />

            <Input
              type="select"
              label="Template Type"
              value={templateType}
              disabled
              options={templateTypes.map((t) => ({ label: t, value: t }))}
            />

            {templateType === "Email" && (
              <Input
                label={
                  <>
                    <span>Subject</span>{" "}
                    <span className={styles["admin-noti-temp__required-star"]}>
                      *
                    </span>
                  </>
                }
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            )}

            <div className={styles["admin-noti-temp__form-group"]}>
              <label className={styles["admin-noti-temp__label"]}>
                Message{" "}
                <span className={styles["admin-noti-temp__required-star"]}>
                  *
                </span>
              </label>
              <Input
                type="textarea"
                style={{ height: "160px" }}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>

            {/* Dynamic Variables Block */}
            {variables.length > 0 && (
              <div className={styles["admin-noti-temp__variables-box"]}>
                <h4 className={styles["admin-noti-temp__variables-title"]}>
                  Available Variables:
                </h4>
                <div className={styles["admin-noti-temp__variables-grid"]}>
                  <ul>
                    {firstHalf.map((v, i) => (
                      <li key={`first-${i}`}>{v}</li>
                    ))}
                  </ul>
                  <ul>
                    {secondHalf.map((v, i) => (
                      <li key={`sec-${i}`}>{v}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className={styles["admin-noti-temp__modal-actions"]}>
              <button
                className={styles["admin-noti-temp__btn"]}
                onClick={handleSaveTemplate}
                disabled={isSaving}
              >
                <Save size={16} strokeWidth={2.5} />
                {isSaving ? "Saving..." : "Save Template"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* PREVIEW HTML MODAL */}
      {showPreview && (
        <Modal onClose={() => setShowPreview(false)} title="Template Preview">
          {previewSubject && previewSubject !== "-" && (
            <h3 className={styles["admin-noti-temp__preview-subject"]}>
              Subject: {previewSubject}
            </h3>
          )}
          <div
            className={styles["admin-noti-temp__preview-content"]}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </Modal>
      )}
    </div>
  );
};

export default NotificationTemplate;
